/**
 * *Read a folder on this computer* — the one control on this page that reaches
 * outside it (§15, §5.25).
 *
 * What it replaces is fourteen fields typed by hand, per repository, from a
 * checkout the person already has open in another window: the remote, the
 * branch, the runtime, the package manager, three commands, a port, a probe,
 * the environment. The app has done this since it existed — you point it at a
 * folder and it fills the form in — and an organization publishing the same
 * project had to type all of it again. Now it is the same reading, through the
 * same rules (`@airlock/contracts/workspace/detect.ts`), so the two agree.
 *
 * ## Nothing is uploaded, and the button says so
 *
 * The folder is read *in this browser*. No byte of it reaches the Worker, and
 * what does leave is only what the person publishes afterwards, out of the form
 * they can see. That is not a detail: a control that asks for a folder full of
 * source code has to say what becomes of it, in the place where it is pressed,
 * and it is the reason the values of secret-looking variables are dropped here
 * rather than carried into a document every member reads.
 *
 * ## Two doors, one button
 *
 * `showDirectoryPicker` is a handle read lazily — a folder with a 4 GB
 * `node_modules` costs nothing, because nothing walks into it. Firefox and
 * Safari have none, so the same button opens a folder input there instead. Its
 * one real weakness is dotfiles: a browser that leaves `.git` out of a
 * directory input gives a reading with no remote, which is why what was found is
 * reported rather than silently accepted.
 */
import { useRef, useState } from 'react';
import type React from 'react';
import { MAX_PROJECTS } from '@airlock/contracts/workspace/document';
import { color, font } from '../../theme/tokens';
import { Note } from '../../components/list';
import { GhostButton } from '../../components/primitives';
import { folderFromFiles, folderPickerKind, pickFolder, type FoundRepo } from '../../lib/repo-folder';
import type { DocProject } from '../../lib/types';
import { prefilled, readRepo } from './detect-repo';
import { newRepo } from './ProjectEditor';

/** What one press of the button concluded, for the sentence underneath it. */
interface Outcome {
  /** How many repositories were read in, and from which folder. */
  added: string[];
  /** Ones already in this workspace, by name — read again would be a duplicate. */
  known: string[];
  /** Per repository, what its reading could not answer. */
  gaps: { name: string; gaps: string[] }[];
  /** Variables whose name was carried and whose value was not, by repository. */
  sealed: { name: string; keys: string[] }[];
  /** Nothing in the folder was a repository at all. */
  empty: boolean;
}

export function FolderScan({
  nextOrder,
  existing,
  onRepos
}: {
  /** Where the rows it adds sort, and how many there is room for. */
  nextOrder: number;
  /** The remotes this workspace already names — read twice is not added twice. */
  existing: readonly string[];
  onRepos: (repos: DocProject[]) => void;
}): React.JSX.Element {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [outcome, setOutcome] = useState<Outcome | null>(null);
  const input = useRef<HTMLInputElement>(null);

  const take = async (found: FoundRepo[]): Promise<void> => {
    if (!found.length) {
      setOutcome({ added: [], known: [], gaps: [], sealed: [], empty: true });
      return;
    }
    const held = new Set(existing.filter(Boolean));
    const repos: DocProject[] = [];
    const next: Outcome = { added: [], known: [], gaps: [], sealed: [], empty: false };
    for (const one of found.slice(0, Math.max(0, MAX_PROJECTS - nextOrder))) {
      const reading = await readRepo(one);
      const made = prefilled(newRepo(nextOrder + repos.length), reading);
      // A repository already in the workspace is *not* overwritten: somebody has
      // edited that row since, and a second reading would quietly undo it.
      if (made.repo.remote && held.has(made.repo.remote)) {
        next.known.push(made.repo.name);
        continue;
      }
      if (made.repo.remote) held.add(made.repo.remote);
      repos.push(made.repo);
      next.added.push(made.repo.name);
      if (made.gaps.length) next.gaps.push({ name: made.repo.name, gaps: made.gaps });
      if (made.sealed.length) next.sealed.push({ name: made.repo.name, keys: made.sealed });
    }
    setOutcome(next);
    if (repos.length) onRepos(repos);
  };

  const run = async (work: () => Promise<FoundRepo[] | null>): Promise<void> => {
    setBusy(true);
    setError(null);
    setOutcome(null);
    try {
      const found = await work();
      // `null` is the picker closed, which is not an outcome to report.
      if (found) await take(found);
    } catch (failure) {
      setError(
        failure instanceof Error && failure.name === 'SecurityError'
          ? 'This browser would not hand that folder over. Try one inside your own documents rather than a system folder.'
          : 'That folder could not be read. Nothing was sent anywhere — try another one, or fill the fields in by hand.'
      );
    } finally {
      setBusy(false);
    }
  };

  const open = (): void => {
    if (folderPickerKind() === 'picker') {
      void run(pickFolder);
      return;
    }
    // The input keeps its last selection, so choosing the same folder twice in a
    // row would fire no `change` at all: it is cleared before it is opened.
    if (input.current) input.current.value = '';
    input.current?.click();
  };

  return (
    <>
      <GhostButton size="sm" onClick={open} disabled={busy || nextOrder >= MAX_PROJECTS}>
        {busy ? 'Reading' : 'Read a folder'}
      </GhostButton>
      <input
        ref={input}
        type="file"
        // Not in React's typings, and the whole of how a folder is asked for
        // where there is no picker.
        {...({ webkitdirectory: '', directory: '' } as Record<string, string>)}
        multiple
        hidden
        onChange={(event) => {
          const files = [...(event.target.files ?? [])];
          if (files.length) void run(() => folderFromFiles(files));
        }}
      />
      {error && (
        <div style={{ flexBasis: '100%' }}>
          <Note tone="warn">{error}</Note>
        </div>
      )}
      {outcome && (
        <div style={{ flexBasis: '100%' }}>
          <Report outcome={outcome} />
        </div>
      )}
    </>
  );
}

function Report({ outcome }: { outcome: Outcome }): React.JSX.Element {
  if (outcome.empty) {
    return (
      <Note tone="warn">
        No git repository in that folder, or in the three levels under it. A project is one repository — point at the
        folder holding its <Mono>.git</Mono>, or at the folder holding several of them.
      </Note>
    );
  }
  return (
    <Note>
      {outcome.added.length > 0 && (
        <>
          Read {outcome.added.length === 1 ? '' : `${outcome.added.length} repositories — `}
          <Mono>{outcome.added.join(', ')}</Mono>.
        </>
      )}
      {outcome.known.length > 0 && (
        <div style={{ marginTop: outcome.added.length ? 8 : 0 }}>
          Already here, and left as they are: <Mono>{outcome.known.join(', ')}</Mono>.
        </div>
      )}
      {outcome.sealed.map((one) => (
        <div key={`sealed-${one.name}`} style={{ marginTop: 8 }}>
          <Mono>{one.name}</Mono> names {one.keys.length} variable{one.keys.length === 1 ? '' : 's'} that look like
          credentials — <Mono>{one.keys.join(', ')}</Mono>. Their names are below; their values stayed on your computer,
          and are set under Secrets where this organization seals them.
        </div>
      ))}
      {outcome.gaps.map((one) => (
        <div key={`gap-${one.name}`} style={{ marginTop: 8 }}>
          <Mono>{one.name}</Mono>: {one.gaps.join(' ')}
        </div>
      ))}
    </Note>
  );
}

function Mono({ children }: { children: React.ReactNode }): React.JSX.Element {
  return <span style={{ fontFamily: font.mono, fontSize: 12.5, color: color.inkBody }}>{children}</span>;
}
